import { api } from '@/lib/api';
import { PLATFORM_TRASH_RETENTION_DAYS } from '@/lib/platformRetention';
import { unwrapData, unwrapPaginated } from '@/lib/api/unwrapData';

import type { Project } from './types';

export async function listProjects(workspaceId?: string): Promise<Project[]> {
  const params = workspaceId ? `?workspaceId=${workspaceId}` : '';
  const response = await api.get<{ data: { projects: Project[]; total: number; page: number; totalPages: number } }>(`/projects${params}`);
  // Backend returns { data: { projects, total, page, totalPages } }
  const paginated = unwrapPaginated<Project>(response);
  return paginated.items;
}

export async function createProject(input: { name: string; workspaceId?: string; templateId?: string }): Promise<Project> {
  const response = await api.post<{ data: Project }>('/projects', input);
  // Backend returns { data: Project }
  return unwrapData<Project>(response) || {} as Project;
}

export async function getProject(idOrWorkspaceId: string, projectId?: string): Promise<Project | null> {
  // Support both getProject(id) and getProject(workspaceId, projectId)
  const id = projectId ?? idOrWorkspaceId;
  const response = await api.get<{ data: Project | null }>(`/projects/${id}`);
  // Backend returns { data: Project | null }
  return unwrapData<Project>(response);
}

export async function renameProject(id: string, name: string): Promise<Project> {
  return api.patch(`/projects/${id}`, { name });
}

/** Move project to another workspace in the same org (PATCH accepts workspaceId). */
export async function moveProjectToWorkspace(
  projectId: string,
  workspaceId: string,
): Promise<Project> {
  const response = await api.patch<{ data: Project }>(`/projects/${projectId}`, { workspaceId });
  return unwrapData<Project>(response) ?? (response as unknown as Project);
}

export async function deleteProject(id: string): Promise<{
  success: true;
  trashRetentionDays: number;
}> {
  const body = (await api.delete(`/projects/${id}`)) as {
    id?: string;
    trashRetentionDays?: number;
  };
  const trashRetentionDays =
    typeof body?.trashRetentionDays === 'number' && body.trashRetentionDays > 0
      ? body.trashRetentionDays
      : PLATFORM_TRASH_RETENTION_DAYS;
  return { success: true, trashRetentionDays };
}

export async function restoreProject(id: string): Promise<Project> {
  return api.post(`/projects/${id}/restore`, {});
}

export async function getProjectsCountByWorkspace(workspaceId: string): Promise<number | null> {
  const res = await api.get(`/projects/stats/by-workspace/${workspaceId}`);
  const payload = unwrapData<{ count?: unknown }>(res) ?? (res as { count?: unknown });
  const count = payload?.count;
  // DASH-WIDGET-FALSE-ZERO-1 — missing count is unknown, not zero.
  return typeof count === 'number' && Number.isFinite(count) ? count : null;
}

